import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/lib/session";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Log ind – Flâneriet" },
      { name: "description", content: "Log ind for at synke din samling på tværs af enheder." },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://flaneriet.online/auth" }],
  }),
  component: AuthPage,
});

function AuthPage() {
  const session = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  // Hvis brugeren allerede er logget ind, gå til samlingen.
  useEffect(() => {
    if (session) {
      navigate({ to: "/bogmaerker", replace: true });
    }
  }, [session, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setState("sending");
    setErrorMsg("");
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: {
          emailRedirectTo: `${window.location.origin}/bogmaerker`,
          shouldCreateUser: true,
        },
      });
      if (error) {
        setState("error");
        setErrorMsg(error.message);
        return;
      }
      setState("sent");
    } catch (err: any) {
      setState("error");
      setErrorMsg(err?.message ?? "Der opstod en fejl. Prøv igen.");
    }
  }

  return (
    <div className="mx-auto max-w-xl">
      <header className="pt-14 pb-8 text-center border-b border-rule">
        <p className="eyebrow">Din samling</p>
        <h1 className="mt-3 font-serif text-[2.4rem] md:text-[3rem] leading-[1.04] text-ink">
          Log ind
        </h1>
        <p className="mt-4 meta italic max-w-md mx-auto">
          Vi sender et link til din email. Ét klik – så er du inde. Ingen kodeord.
        </p>
      </header>

      <section className="py-12">
        {state === "sent" ? (
          <div className="text-center">
            <p className="font-serif text-[1.15rem] md:text-[1.25rem] leading-[1.65] text-ink">
              Vi har sendt et link til <em>{email}</em>. Åbn mailen på samme enhed for at komme ind.
            </p>
            <p className="mt-6 meta italic">
              Intet i indbakken? Se i spam, eller <button onClick={() => setState("idle")} className="underline decoration-rule underline-offset-2 hover:text-bordeaux hover:decoration-bordeaux transition-colors">prøv en anden email</button>.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5 max-w-md mx-auto">
            <label className="block">
              <span className="eyebrow block mb-2">Email</span>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="dig@eksempel.dk"
                className="w-full font-serif text-[16px] py-3 px-4 border border-ink bg-transparent text-ink outline-none focus:border-bordeaux transition-colors"
              />
            </label>
            <button
              type="submit"
              disabled={state === "sending" || !email.trim()}
              className="w-full font-serif text-[15px] py-3 px-4 border border-bordeaux text-bordeaux hover:bg-bordeaux/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {state === "sending" ? "Sender …" : "Send login-link"}
            </button>
            {state === "error" && (
              <p className="meta italic text-bordeaux">{errorMsg}</p>
            )}
            <p className="meta italic pt-4 border-t border-rule">
              Dine bogmærker fungerer også uden login – de gemmes lokalt i browseren.
              Log ind for at synke dem på tværs af enheder. <Link to="/bogmaerker" className="underline decoration-rule underline-offset-2 hover:text-bordeaux hover:decoration-bordeaux transition-colors">Tilbage til samlingen</Link>.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}
